import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Button } from '@upmc/ims.component-library';
import { UserContext } from 'context/User/UserContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from '../../components/UI/MDC/DataTable/DataTable';
import {
	Subtitle1,
	Subtitle2,
} from '../../components/UI/MDC/Typography/Typography';
import { NOTIFICATION_TYPES } from '../../constants/notifications';
import { addMessage, clearMessages } from '../../store/ancillary/actions';
import { processPendingApprovalsRequest } from './_api';
import { HEADERS } from './_constants';
import classes from './PendingApprovals.module.scss';
import { Box } from '@mui/system';

/**
 * React component for listing selected approvals and prompting for user confirmation before handling batch processing.
 * @param {object} props Props for opening and closing the modal.
 * @returns React component.
 */
const ConfirmationModal = ({
	confirmationModalOpen,
	setConfirmationModalOpen,
	setResultsRows,
	refreshTable,
	lockTable,
}) => {
	// Redux store
	const rows = useSelector(state => state.pendingApprovals.rows);

	const [selectedRows, setSelectedRows] = useState([]);

	const dispatch = useDispatch();

	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const processBulkApprovals = useCallback(() => {
		// Lock table by forcing it to load to avoid tampering and start loading spinner in modal
		lockTable();
		setConfirmationModalOpen(false);

		// Display banner message
		dispatch(
			addMessage({
				Key: NOTIFICATION_TYPES.INFO,
				Value: 'Your request is currently being processed.',
			})
		);

		// Send request and process results
		processPendingApprovalsRequest(
			selectedRows.map(row => ({
				EventFid: row.Key,
				ApprovalMqLabelType: row.approvalType,
				EsoId: user.ESOID,
				TrackingNumber: row.Cells[HEADERS.HEADER_TRACKING_NUMBER].Value,
			}))
		)
			.then(payload => {
				// Go through results
				setResultsRows(
					payload.map(row => ({
						Cells: {
							[HEADERS.HEADER_TRACKING_NUMBER]: {
								Value: row.trackingNumber,
								Validation: {
									Key: 'true',
									Value: null,
									memberToValidateKey: null,
								},
								props: {
									alignMiddle: true,
									extendtext: 13,
								},
							},
							Message: {
								Value: row.message,
								Validation: {
									Key: 'true',
									Value: null,
									memberToValidateKey: null,
								},
								props: {
									alignMiddle: true,
								},
							},
						},
						Key: row.trackingNumber,
						success: row.success,
					}))
				);
			})
			.catch(e =>
				// Display error in case of exception
				dispatch(
					addMessage({
						Key: NOTIFICATION_TYPES.ERROR,
						Value: `An error has occurred while attempting to process your request. ${e.message}.`,
					})
				)
			)
			.finally(() => {
				refreshTable();
				dispatch(clearMessages());
			});
	}, [
		lockTable,
		setConfirmationModalOpen,
		selectedRows,
		user.ESOID,
		setResultsRows,
		dispatch,
		refreshTable,
	]);

	// Update displayed and selected rows
	useEffect(() => {
		if (confirmationModalOpen) {
			// Update selected rows
			const newSelectedRows = rows
				.filter(
					row =>
						row.ActionCells[0].Checked &&
						row.displaySearched &&
						row.displayFiltered
				)
				.map(row => ({
					Cells: {
						[HEADERS.HEADER_REQUEST_FOR]: {
							Value: row.Cells[HEADERS.HEADER_REQUEST_FOR].Value,
							Validation: {
								Key: 'true',
								Value: null,
								memberToValidateKey: null,
							},
							props: {
								alignMiddle: true,
							},
						},
						[HEADERS.HEADER_TRACKING_NUMBER]: {
							Value: row.Cells[HEADERS.HEADER_TRACKING_NUMBER]
								.Value,
							Validation: {
								Key: 'true',
								Value: null,
								memberToValidateKey: null,
							},
							props: {
								alignMiddle: true,
								extendtext: 13,
							},
						},
					},
					Key: row.Key,
					approvalType: row.approvalType,
				}));
			setSelectedRows(newSelectedRows);
		}
	}, [confirmationModalOpen, rows]);

	// Return React component
	return (
		<Dialog
			open={confirmationModalOpen}
			onClose={() => setConfirmationModalOpen(false)}
			className={classes.dialog__container}
		>
			<DialogTitle>Bulk Approve</DialogTitle>
			<DialogContent className={classes.dialog__content}>
				{selectedRows.length > 5 && (
					<Alert severity='warning' sx={{ width: '100%' }}>
						Warning: You have selected a large number of approvals.{' '}
						Processing may take <b>half a minute</b> or more.
					</Alert>
				)}

				<Subtitle1>
					Performing this action will <b>bulk approve</b> the
					following request(s) <b>without</b> comments.
				</Subtitle1>

				<Box sx={{ width: '100%' }}>
					<DataTable
						headers={[
							{
								Value: HEADERS.HEADER_REQUEST_FOR,
								props: {
									alignMiddle: true,
								},
							},
							{
								Value: HEADERS.HEADER_TRACKING_NUMBER,
								props: {
									alignMiddle: true,
								},
							},
						]}
						rows={selectedRows}
					/>
				</Box>

				<Subtitle2>
					Note: If you do not wish to continue, you may close this
					message and manually approve or decline requests.
				</Subtitle2>
			</DialogContent>

			<DialogActions>
				<Button
					variant='text'
					onClick={() => setConfirmationModalOpen(false)}
				>
					Close
				</Button>
				<Button variant='text' onClick={() => processBulkApprovals()}>
					Continue
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export { ConfirmationModal };
